import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase,
  User
} from "@models";
import { 
  SourceBook,
  Campaign,
  Character,
  // GameClass,
  // Gift,
  // Race,
  // Subclass,
  // Subrace,
  CharacterOptions,
  UpgradableNumber,
  Subclass,
  WEAPON_KEYWORD_OPTIONS,
} from "@dndModels";
import { DATA_TYPE_INFO, DICE_SIZE_OPTIONS, WORLD_SETTINGS } from "@dndModels/Constants";
import { DominanceGroupType, WorldSetting } from "@dndModels/Types";

import API from "@dndUtilities/system_api";
// import DataUtilities from "../../../../../utilities/common_utilities";
// import DandDCharacterUtilities from '../../../utilities/character_utilities';

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import FloatMenu from "@components/input/menus/FloatMenu";
import LoadingBox from "@components/input/LoadingBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CheckBox from "@components/input/CheckBox";
import PageLinks from "@components/Navigation/PageLinks";
import FilterBox from "@dndComponents/filtering/FilterBox";
import LetterLinks from "@components/Navigation/LetterLinks";
import SelectSourceBookBox from '@dndComponents/select/SelectSourceBookBox';
import CommonUtilities from '@utilities/common_utilities';

interface Props {
  obj: Character | Campaign;
  disabled: boolean;
  onChange: (options: CharacterOptions) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterOptionsInput({
  obj,
  disabled,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [source_books, setSourceBooks] = useState<SourceBook[]>([]);
  const [players, setPlayers] = useState<User[]>([]);
  const [purchased_sb_ids, setPurchasedSBIds] = useState<string[]>([]);
  const [character_options, setCharacterOptions] = useState(obj.character_options);
  const [view, setView] = useState("Allowed Sources");
  const [show_archives, setShowArchives] = useState(false);
  const [show_homebrew, setShowHomebrew] = useState(false);
  const [show_third_party, setShowThirdParty] = useState(false);
  const [standard_array_string, setStandardArrayString] = useState(obj.character_options.standard_array.map(o => `${o}`).join(","));
  const [reload_standard_array, setReloadStandardArray] = useState(false);
  const [sb_type, setSBType] = useState("All");
  const [blocking_data_type, setBlockingDataType] = useState<"Race/Species" | "Subrace/Lineage" | "Class" | "Subclass" | "Feat" | "Gift" | "Item" | "Magic Item Keyword" | "Spell" | "">("");
  const [loading_blockables, setLoadingBlockables] = useState(false);
  const [blockable_options, setBlockableOptions] = useState<ModelBase[]>([]);
  const [page_num, setPageNum] = useState(0);
  const [start_letter, setStartLetter] = useState("");
  const [block_filter, setBlockFilter] = useState<any>({});
  const [filtering, setFiltering] = useState(false);
  const [prev_startLetter, setPrevStartLetter] = useState("");
  const [filtered, setFiltered] = useState<ModelBase[]>([]);
  const [display_unowned, setDisplayUnowned] = useState(false);

  const exhaustionFilter = useMemo(() => ({ exhaustion: true }), []);
  const encumbranceFilter = useMemo(() => ({ encumbrance: true }), []);
  const conditionFilter = useMemo(() => ({ always_show: false }), []);

  const load_players = useCallback( async () => {
    // debugger;
    const filter: any = {};
    if (obj instanceof Campaign) {
      filter["_id*ObjectIDs"] = [...obj.player_ids];
    } else if (obj.campaign) {
      filter["_id*ObjectIDs"] = [...obj.campaign.player_ids];
    }
    console.log(filter);
    if (Object.keys(filter).length === 0) {
      setPlayers(api.user ? [api.user] : []);
      setPurchasedSBIds(api.user?.source_book_ids??[]);
      setLoading(false);
    } else {
      const res = await api.getObjects<User>({data_type: "user", filter});
      if (res && api.user) {
        const new_players: User[] = [api.user,...res];
        let new_purchased_sb_ids: string[] = [];
        for (const player of new_players) {
          new_purchased_sb_ids = [...new_purchased_sb_ids,...player.source_book_ids];
        }
        setPlayers(new_players);
        setPurchasedSBIds(new_purchased_sb_ids);
        setLoading(false);
      }
    }
  }, []);

  const load = useCallback( async () => {
    setLoading(true);
    // console.log(api.user?.source_book_ids??[]);
    const res = await api.getSetOfObjects({ data_types: ["source_book"] });
    setSourceBooks(res.source_book as SourceBook[]);
    await load_players();
  }, [load_players]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const is_disabled = disabled || (obj instanceof Character && obj.campaign !== null) || api.user === null || (!api.user.admin && obj.owner_id !== api.user._id);
  
  const renderSource = (book: SourceBook) => {
    const disabled = ((!api.user || !api.user.is_admin) && !purchased_sb_ids.includes(book._id)) || is_disabled;
    return (
      <Grid
        key={book._id}
        className="padded"
        size={{
          xs: 4
        }}>
        { !reloading &&
          <ToggleButtonBox 
            name={book.name}
            value={character_options.source_books.includes(book._id)} 
            disabled={disabled}
            onToggle={() => {
              const new_character_options = character_options;
              if (new_character_options.source_books.includes(book._id)) {
                new_character_options.source_books = new_character_options.source_books.filter(o => o !== book._id);
              } else {
                new_character_options.source_books.push(book._id);
              }
              setCharacterOptions(new_character_options);
              setReloading(true);
            }}
          />
        }
      </Grid>
    );
  }

  const renderSources = () => {
    const use_sbs = source_books.filter(o => display_unowned !== purchased_sb_ids.includes(o._id));
    if (show_homebrew) {
      let homebrews = use_sbs.filter(o => o.homebrew);
      homebrews = homebrews.filter(o => 
        (o.is_free ||
          purchased_sb_ids.includes(o._id) ||
          players.filter(o2 => o2._id === o.owner_id).length === 1)
      );
      return (
        <>
          <Grid
            className="header"
            size={{
              xs: 12
            }}>Source Books</Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { !reloading && homebrews.map(book => {
              return renderSource(book);
            })}
          </Grid>
          <Grid
            className="header"
            size={{
              xs: 12
            }}>Personal Homebrews</Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { !reloading && players.map((player, key) => {
              return (
                <Grid
                  key={key}
                  className="padded"
                  size={{
                    xs: 4
                  }}>
                  <ToggleButtonBox 
                    name={`${player.profile_name} Personal Homebrews`}
                    value={character_options.source_books.includes(player._id)} 
                    disabled={is_disabled}
                    onToggle={() => {
                      const new_character_options = character_options;
                      if (new_character_options.source_books.includes(player._id)) {
                        new_character_options.source_books = new_character_options.source_books.filter(o => o !== player._id);
                      } else {
                        new_character_options.source_books.push(player._id);
                      }
                      setCharacterOptions(new_character_options);
                      setReloading(true);
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </>
      ); 
    } else {
      let sources = use_sbs.filter(o => !o.homebrew && o.third_party === show_third_party && o.archived === show_archives);
      if (sb_type !== "All") {
        sources = sources.filter(o => o.source_book_type === sb_type);
      }
      return (
        <>
          { sources.filter(o => o.source_book_type === "Source Book").length > 0 && 
            <>
              <Grid
                className="header"
                size={{
                  xs: 12
                }}>Source Books</Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                { !reloading && sources.filter(o => o.source_book_type === "Source Book").map(book => {
                  return renderSource(book);
                })}
              </Grid>
            </>
          }
          { sources.filter(o => o.source_book_type === "Setting").length > 0 && 
            <>
              <Grid
                className="header"
                size={{
                  xs: 12
                }}>Setting Books</Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                { !reloading && sources.filter(o => o.source_book_type === "Setting").map(book => {
                  return renderSource(book);
                })}
              </Grid>
            </>
          }
          { sources.filter(o => o.source_book_type === "Adventure").length > 0 && 
            <>
              <Grid
                className="header"
                size={{
                  xs: 12
                }}>Adventure Books</Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                { !reloading && sources.filter(o => o.source_book_type === "Adventure").map(book => {
                  return renderSource(book);
                })}
              </Grid>
            </>
          }
          { sources.filter(o => o.source_book_type === "Unearthed Arcana").length > 0 && 
            <>
              <Grid
                className="header"
                size={{
                  xs: 12
                }}>Unearthed Arcana</Grid>
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                { !reloading && sources.filter(o => o.source_book_type === "Unearthed Arcana").map(book => {
                  return renderSource(book);
                })}
              </Grid>
            </>
          }
        </>
      );
    }
  }

  useEffect(() => {
    if (start_letter !== prev_startLetter) {
      updateBlockFilter(block_filter);
      setPrevStartLetter(start_letter);
    }
    // eslint-disable-next-line
  }, [start_letter, prev_startLetter, setPrevStartLetter]);

  useEffect(() => {
    if (filtering) {
      refilter();
      setFiltering(false);
    }
    // eslint-disable-next-line
  }, [filtering]);

  const refilter = () => {
    // debugger;
    const dt_info = DATA_TYPE_INFO.find(o => o.name === blocking_data_type);
    if (dt_info) {
      let new_filtered = [...blockable_options];
      const the_filter = {...block_filter};
      if (the_filter.start_letter) {
        the_filter["name*startsWith"] = the_filter.start_letter;
        delete the_filter.start_letter;
      }

      // console.log(new_filtered);
      // console.log(the_filter);
      new_filtered = api.filter("", new_filtered, the_filter) as ModelBase[];
      // console.log(new_filtered);
      if (!obj.character_options.use_spell_infusions) {
        if (blocking_data_type === "Class") {
          new_filtered = new_filtered.filter(o => o._id !== "66fc8ff698e22f79628ce5d8");
        } else if (blocking_data_type === "Subclass") {
          new_filtered = (new_filtered as Subclass[]).filter(o => o.game_class_id !== "66fc8ff698e22f79628ce5d8");
        }
      }
      if (obj.character_options.dominant_variants_only.includes(dt_info.dominance_group)) {
        new_filtered = new_filtered.filter(o => o.isDominant());
      }
      setFiltered(new_filtered);
    }
  }

  const updateBlockFilter = (the_filter: any) => {
    const new_filter = {...the_filter};
    
    if (start_letter !== "") {
      new_filter["name*startsWith"] = start_letter;
    } else {
      delete new_filter["name*startsWith"];
    }
    setBlockFilter(new_filter);
    setPageNum(0);
    setStartLetter("");
    setFiltering(true);
  }

  const render_tab = () => {
    let return_me = <Grid>Coming Soon</Grid>;
    const user = api.user;
    if (user) {
      if (view === "Special Rules") {
        console.log("Rendering Special Rules");
        return_me = (
          <Grid size={12}
            container spacing={0} direction="row">
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="UA Content"
                value={character_options.allow_UA} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.allow_UA = !new_character_options.allow_UA;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="3rd Party Content"
                value={character_options.allow_3rd_party} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.allow_3rd_party = !new_character_options.allow_3rd_party;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Archived Content"
                value={character_options.allow_archives} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.allow_archives = !new_character_options.allow_archives;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Homebrew Content"
                value={character_options.allow_homebrew} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.allow_homebrew = !new_character_options.allow_homebrew;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Use Suggestions"
                disabled={is_disabled}
                value={character_options.use_suggestions} 
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_suggestions = !new_character_options.use_suggestions;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Monster Content"
                disabled={is_disabled}
                value={character_options.allow_monster_feature_sources} 
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.allow_monster_feature_sources = !new_character_options.allow_monster_feature_sources;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Optional Features"
                disabled={is_disabled}
                value={character_options.optional_features} 
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.optional_features = !new_character_options.optional_features;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectObjectBox 
                name="Show Extra Conditions"
                data_type="condition"
                filter={conditionFilter}
                multiple
                values={character_options.show_extra_conditions} 
                disabled={is_disabled}
                onChange={(changed: string[]) => {
                  const new_character_options = character_options;
                  new_character_options.show_extra_conditions = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectSourceBookBox 
                name="Major Spell List Source Book"
                type={character_options.default_major_spell_list_sb.type}
                id={character_options.default_major_spell_list_sb.id} 
                disabled={is_disabled}
                onChange={(type, id) => {
                  const new_character_options = character_options;
                  new_character_options.default_major_spell_list_sb.type = type;
                  new_character_options.default_major_spell_list_sb.id = id;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Free Weapon Profs"
                options={WEAPON_KEYWORD_OPTIONS}
                multiple
                values={character_options.free_weapon_profs} 
                disabled={is_disabled}
                onChange={(changed: string[]) => {
                  const new_character_options = character_options;
                  new_character_options.free_weapon_profs = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Allow Players to Control Their Gifts"
                value={character_options.player_control_for_gifts} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.player_control_for_gifts = !new_character_options.player_control_for_gifts;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Bonus Skill Profs for INT"
                value={character_options.bonus_skill_profs_for_int} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.bonus_skill_profs_for_int = !new_character_options.bonus_skill_profs_for_int;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Level 0"
                value={character_options.allow_level_0} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.allow_level_0 = !new_character_options.allow_level_0;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            { character_options.allow_level_0 &&
              <>
                {/* <Grid item xs={3}>
                  <SelectStringBox 
                    name="Level 0.X Weapon Proficiencies"
                    option_map={{ "Full": 1, "Half": 0.5, "None": 0 }}
                    value={character_options.level_0X_weapon_profs} 
                    disabled={is_disabled}
                    onChange={(changed: string) => {
                      const character_options = character_options;
                      character_options.level_0X_weapon_profs = +changed;
                      this.setState({ character_options });
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <SelectStringBox 
                    name="Level 0.X Armor Proficiencies"
                    option_map={{ "Full": 1, "Half": 0.5, "None": 0 }}
                    value={character_options.level_0X_armor_profs} 
                    disabled={is_disabled}
                    onChange={(changed: string) => {
                      const character_options = character_options;
                      character_options.level_0X_armor_profs = +changed;
                      this.setState({ character_options });
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <SelectStringBox 
                    name="Level 0.X Save Proficiencies"
                    option_map={{ "Full": 1, "Half": 0.5, "None": 0 }}
                    value={character_options.level_0X_save_profs} 
                    disabled={is_disabled}
                    onChange={(changed: string) => {
                      const character_options = character_options;
                      character_options.level_0X_save_profs = +changed;
                      this.setState({ character_options });
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <SelectStringBox 
                    name="Level 0.X Skill Proficiencies"
                    option_map={{ "Full": 1, "Half": 0.5, "None": 0 }}
                    value={character_options.level_0X_skill_profs} 
                    disabled={is_disabled}
                    onChange={(changed: string) => {
                      const character_options = character_options;
                      character_options.level_0X_skill_profs = +changed;
                      this.setState({ character_options });
                    }}
                  />
                </Grid> */}
              </>
            }
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Potions as Bonus Actions"
                value={character_options.potions_ba} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.potions_ba = !new_character_options.potions_ba;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Potions Heal Max on Action"
                value={character_options.potion_max_on_action} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.potion_max_on_action = !new_character_options.potion_max_on_action;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Leveled Potions"
                value={character_options.use_leveled_potions} 
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_leveled_potions = !new_character_options.use_leveled_potions;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            { character_options.use_leveled_potions &&
              <>
                <Grid size={4} className="padded">
                  <SelectStringBox 
                    name="Leveled Potion Dice Size"
                    options={DICE_SIZE_OPTIONS}
                    value={`${character_options.leveled_potion_dice_size}`} 
                    disabled={is_disabled}
                    onChange={(changed: string) => {
                      const new_character_options = character_options;
                      new_character_options.leveled_potion_dice_size = +changed;
                      setCharacterOptions(new_character_options);
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={4} className="padded">
                  <StringBox 
                    name="Leveled Potion Bonus Multiplier"
                    value={`${character_options.leveled_potion_bonus_multiplier}`} 
                    type="number"
                    disabled={is_disabled}
                    onBlur={(changed: string) => {
                      const new_character_options = character_options;
                      new_character_options.leveled_potion_bonus_multiplier = +changed;
                      setCharacterOptions(new_character_options);
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={4} className="padded">
                  <StringBox 
                    name="Leveled Potion Max Size"
                    value={`${character_options.leveled_potion_max_size}`} 
                    type="number"
                    disabled={is_disabled}
                    onBlur={(changed: string) => {
                      const new_character_options = character_options;
                      new_character_options.leveled_potion_max_size = +changed;
                      setCharacterOptions(new_character_options);
                      setPing(!ping);
                    }}
                  />
                </Grid>
              </>
            }
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Generation Method"
                options={["Any","Not Manual/Rolled","Point Buy","Standard Array","Manual/Rolled"]}
                value={ character_options.ability_score_gen_method } 
                onChange={(changed: string) => {
                  const new_character_options = character_options;
                  switch(changed) {
                    case "Any": new_character_options.ability_score_gen_method = "Any"; break;
                    case "Not Manual/Rolled": new_character_options.ability_score_gen_method = "Not Manual/Rolled"; break;
                    case "Point Buy": new_character_options.ability_score_gen_method = "Point Buy"; break;
                    case "Standard Array": new_character_options.ability_score_gen_method = "Standard Array"; break;
                    case "Manual/Rolled": new_character_options.ability_score_gen_method = "Manual/Rolled"; break;
                  }
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              { ["Any","Not Manual/Rolled","Point Buy"].includes(character_options.ability_score_gen_method) && 
                <StringBox 
                  name="Point Buy Points"
                  value={ `${character_options.points}` } 
                  type="number"
                  onBlur={(changed: string) => {
                    const new_character_options = character_options;
                    new_character_options.points = +changed;
                    setCharacterOptions(new_character_options);
                    setPing(!ping);
                  }}
                />
              }
            </Grid>
            <Grid size={4} className="padded">
              { ["Any","Not Manual/Rolled","Point Buy"].includes(character_options.ability_score_gen_method) && 
                <StringBox 
                  name="Point Buy Mapping (Ability Score: Points)"
                  value={ Object.keys(character_options.point_mapping).map(o => { return `${o}: ${character_options.point_mapping[+o]}`; }).join(", ") } 
                  multiline
                  onBlur={(changed: string) => {
                    try {
                      let good = true;
                      const point_mapping: { [ability_score: number]: number } = {};
                      const pieces = changed.split(",");
                      for (const score_piece of pieces) {
                        const score_pieces = score_piece.split(":");
                        if (score_pieces.length !== 2) {
                          good = false;
                          break;
                        }
                        const score = score_pieces[0].trim();
                        const points = score_pieces[1].trim();
                        if (isNaN(+score) || isNaN(+points)) {
                          good = false;
                          break;
                        }
                        point_mapping[+score] = +points;
                      }
                      if (good) {
                        const new_character_options = character_options;
                        new_character_options.point_mapping = point_mapping;
                        setCharacterOptions(new_character_options);
                        setPing(!ping);
                      }
                    } catch (ex) { }
                  }}
                />
              }
            </Grid>
            <Grid size={4} className="padded">
              { ["Any","Not Manual/Rolled","Standard Array"].includes(character_options.ability_score_gen_method) && !reload_standard_array && 
                <StringBox 
                  name="Standard Array"
                  value={ standard_array_string } 
                  onBlur={(changed: string) => {
                    const pieces = changed.split(",");
                    if (pieces.length === 6) {
                      const standard_array: number[] = [];
                      let good = true;
                      for (let i = 0; i < pieces.length; ++i) {
                        const num_str = pieces[i];
                        try {
                          const num = +num_str;
                          if ([1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].includes(num)) {
                            standard_array.push(num);
                          } else {
                            good = false;
                            break;
                          }
                        } catch (exception) {
                          console.error(exception);
                          good = false;
                          break;
                        }
                      }
                      if (good) {
                        const new_character_options = character_options;
                        new_character_options.standard_array = standard_array;
                        setCharacterOptions(new_character_options);
                        setStandardArrayString(changed);
                      } else {
                        setReloadStandardArray(true);
                      }
                    } else {
                      setReloadStandardArray(true);
                    }
                    setPing(!ping);
                  }}
                />
              }
            </Grid>
            <Grid size={4} className="padded">
              {/* <ToggleButtonBox 
                name="Show Extra Action Types"
                value={character_options.show_extra_action_types} 
                onToggle={() => {
                  const character_options = character_options;
                  character_options.show_extra_action_types = !character_options.show_extra_action_types;
                  this.setState({ character_options }, () => {
                    if (is_disabled) {
                      // Auto save and repopulate
                      onChange(character_options);
                    }
                  });
                }}
              /> */}
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Display Unfit Items"
                values={ character_options.display_unfit_items } 
                options={["Feats","Eldritch Invocations"]}
                multiple
                onChange={(changed: string[]) => {
                  const new_character_options = character_options;
                  new_character_options.display_unfit_items = changed;
                  setCharacterOptions(new_character_options);
                  if (disabled) {
                    onChange(character_options);
                  }
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Ignore Limitations"
                values={ character_options.ignore_limitations } 
                options={["Feats","Eldritch Invocations"]}
                multiple
                disabled={is_disabled}
                onChange={(changed: string[]) => {
                  const new_character_options = character_options;
                  new_character_options.ignore_limitations = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Use Coin Weight"
                disabled={is_disabled}
                value={character_options.use_coin_weight} 
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_coin_weight = !new_character_options.use_coin_weight;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox 
                name="Use Encumbrance Effects"
                disabled={is_disabled}
                value={character_options.use_encumbrance_effects} 
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_encumbrance_effects = !new_character_options.use_encumbrance_effects;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectObjectBox 
                name="Encumbrance Condition"
                data_type="condition"
                filter={encumbranceFilter}
                disabled={is_disabled}
                value={character_options.encumbrance_id} 
                onChange={(changed: string) => {
                  const new_character_options = character_options;
                  new_character_options.encumbrance_id = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Races Called"
                options={["Race","Species"]}
                disabled={is_disabled}
                value={character_options.races_called} 
                onChange={(changed: "Race" | "Species") => {
                  const new_character_options = character_options;
                  new_character_options.races_called = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            {/* <Grid item xs={4} className="padded">
              <SelectStringBox 
                name="ASIs On"
                options={["Race/Species","Background"]}
                disabled={is_disabled}
                value={character_options.asis_on} 
                onChange={(changed: "Race/Species" | "Background") => {
                  const new_character_options = character_options;
                  new_character_options.asis_on = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid> */}
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Light Melee Casting Time"
                options={["Action","Bonus Action"]}
                disabled={is_disabled}
                value={character_options.light_melee_action} 
                onChange={(changed: "Action" | "Bonus Action") => {
                  const new_character_options = character_options;
                  new_character_options.light_melee_action = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox 
                name="Heavy Weapon Rule"
                options={["Disadvantage from Size","Disadvantage from Ability Score"]}
                disabled={is_disabled}
                value={character_options.heavy_weapon_rule} 
                onChange={(changed: "Disadvantage from Size" | "Disadvantage from Ability Score") => {
                  const new_character_options = character_options;
                  new_character_options.heavy_weapon_rule = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectObjectBox 
                name="Exhaustion Condition"
                data_type="condition"
                filter={exhaustionFilter}
                disabled={is_disabled}
                value={character_options.exhaustion_id} 
                // log_it
                onChange={(changed: string) => {
                  const new_character_options = character_options;
                  new_character_options.exhaustion_id = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectObjectBox 
                name="Standard Features"
                data_type="standard_features"
                disabled={is_disabled}
                value={character_options.standard_features_id} 
                onChange={(changed: string) => {
                  const new_character_options = character_options;
                  new_character_options.standard_features_id = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox
                name="Use Turn Resources"
                value={character_options.use_turn_resources}
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_turn_resources = !new_character_options.use_turn_resources;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <UpgradableNumberBox
                name="Attunement Slots"
                value={character_options.attunement_slots}
                disabled={is_disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_character_options = character_options;
                  new_character_options.attunement_slots = changed.clone();
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox
                name="Use Mini/Night Rests"
                value={character_options.use_mini_night_rests}
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_mini_night_rests = !new_character_options.use_mini_night_rests;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            { character_options.use_mini_night_rests ? 
              <>
                <Grid size={4} className="padded">
                  <StringBox
                    name="Mini Rest Description"
                    value={character_options.mini_rest_description}
                    disabled={is_disabled}
                    multiline
                    onBlur={(changed: string) => {
                      const new_character_options = character_options;
                      new_character_options.mini_rest_description = changed;
                      setCharacterOptions(new_character_options);
                      setPing(!ping);
                    }}
                  />
                </Grid>
                <Grid size={4} className="padded">
                  <StringBox
                    name="Night Rest Description"
                    value={character_options.night_rest_description}
                    disabled={is_disabled}
                    multiline
                    onBlur={(changed: string) => {
                      const new_character_options = character_options;
                      new_character_options.night_rest_description = changed;
                      setCharacterOptions(new_character_options);
                      setPing(!ping);
                    }}
                  />
                </Grid>
              </>
            :
              <Grid size={4} className="padded">
                <StringBox
                  name="Short Rest Description"
                  value={character_options.short_rest_description}
                  disabled={is_disabled}
                  multiline
                  onBlur={(changed: string) => {
                    const new_character_options = character_options;
                    new_character_options.short_rest_description = changed;
                    setCharacterOptions(new_character_options);
                    setPing(!ping);
                  }}
                />
              </Grid>
            }
            <Grid size={4} className="padded">
              <StringBox
                name="Long Rest Description"
                value={character_options.long_rest_description}
                disabled={is_disabled}
                multiline
                onBlur={(changed: string) => {
                  const new_character_options = character_options;
                  new_character_options.long_rest_description = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <CheckBox
                name="Allow Multiclassing"
                value={character_options.allow_multiclassing}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.allow_multiclassing = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            { character_options.allow_multiclassing &&
              <Grid size={4} className="padded">
                <CheckBox
                  name="Ignore Multiclassing Rules"
                  value={character_options.ignore_multiclassing_rules}
                  disabled={is_disabled}
                  onChange={(changed: boolean) => {
                    const new_character_options = character_options;
                    new_character_options.ignore_multiclassing_rules = changed;
                    setCharacterOptions(new_character_options);
                    setPing(!ping);
                  }}
                />
              </Grid>
            }
            <Grid size={4} className="padded">
              <CheckBox
                name="Use Item Crafting"
                value={character_options.use_item_crafting}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.use_item_crafting = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox
                name="Dominant Variants Only"
                options={["Race/Species","Subrace/Lineage","Background","Class","Subclass","Eldritch Invocation","Feat","Item","Spell","Other"]}
                values={character_options.dominant_variants_only}
                disabled={is_disabled}
                multiple
                onChange={(changed: DominanceGroupType[]) => {
                  const new_character_options = character_options;
                  new_character_options.dominant_variants_only = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <CheckBox
                name="Make Single Option Feat Features Flexible"
                value={character_options.single_option_feat_flexibility}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.single_option_feat_flexibility = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <CheckBox
                name="Make ASI Features Flexible"
                value={character_options.asi_flexibility}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.asi_flexibility = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <CheckBox
                name="Use Bastions"
                value={character_options.use_bastions}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.use_bastions = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <CheckBox
                name="Use Minion HP Calculations"
                value={character_options.use_minion_hp_calcs}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.use_minion_hp_calcs = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <CheckBox
                name="Use Wild Talents"
                value={character_options.allow_wild_talent}
                disabled={is_disabled}
                onChange={(changed: boolean) => {
                  const new_character_options = character_options;
                  new_character_options.allow_wild_talent = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <SelectStringBox
                name="World Settings"
                options={WORLD_SETTINGS}
                extra_options={["Any"]}
                values={character_options.world_settings}
                disabled={is_disabled}
                onChange={(changed: WorldSetting[]) => {
                  const new_character_options = character_options;
                  new_character_options.world_settings = changed;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
            <Grid size={4} className="padded">
              <ToggleButtonBox
                name="Use Spell Infusions"
                value={character_options.use_spell_infusions}
                disabled={is_disabled}
                onToggle={() => {
                  const new_character_options = character_options;
                  new_character_options.use_spell_infusions = !new_character_options.use_spell_infusions;
                  setCharacterOptions(new_character_options);
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
        ); 
      } else if (view === "Allowed Sources") {
        // if (sb_view === "Normal") {
        return_me = (
          <Grid size={12}
            container spacing={0} direction="row">
            <Grid size={4}>
              <SelectStringBox
                name="Filter by Source Book Type"
                options={ character_options.allow_UA ? ["All","Source Book","Setting","Adventure","Unearthed Arcana"] : ["All","Source Book","Setting","Adventure"] }
                value={sb_type}
                onChange={(changed: string) => {
                  setSBType(changed);
                  setReloading(true);
                }}
              />
            </Grid>
            <Grid size={2}>
              <ToggleButtonBox 
                name="Display Unowned"
                value={display_unowned} 
                onToggle={() => {
                  setDisplayUnowned(!display_unowned);
                }}
              />
            </Grid>
            <Grid size={2}>
              { character_options.allow_3rd_party &&
                <ToggleButtonBox 
                  name="Show 3rd Party Content"
                  value={show_third_party} 
                  onToggle={() => {
                    setShowThirdParty(!show_third_party);
                    // setShowArchives(false);
                    // setShowHomebrew(false);
                  }}
                />
              }
            </Grid>
            <Grid size={2}>
              { character_options.allow_archives &&
                <ToggleButtonBox 
                  name="Show Archived Content"
                  value={show_archives} 
                  onToggle={() => {
                    // setShowThirdParty(false);
                    setShowArchives(!show_archives);
                    // setShowHomebrew(false);
                  }}
                />
              }
            </Grid>
            <Grid size={2}>
              { character_options.allow_homebrew &&
                <ToggleButtonBox 
                  name="Show Homebrew Content"
                  value={show_homebrew} 
                  onToggle={() => {
                    // setShowThirdParty(false);
                    // setShowArchives(false);
                    setShowHomebrew(!show_homebrew);
                  }}
                />
              }
            </Grid>
            { renderSources() }
          </Grid>
        ); 
      } else {
        if (view === "Blocked Options") {
          const dt_info = DATA_TYPE_INFO.find(o => o.name === blocking_data_type);
          const page_size = 21;
          const page_count = Math.ceil(filtered.length / page_size);
          const filtered_and_paged = filtered.slice(page_size * page_num, page_size * (page_num + 1));
          return_me = (
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <SelectStringBox
                  name="Type of thing to Block"
                  options={["Race/Species","Subrace/Lineage","Class","Subclass","Feat","Gift","Item","Magic Item Keyword","Spell"]}
                  value={blocking_data_type}
                  disabled={loading_blockables}
                  onChange={(changed: "Race/Species" | "Subrace/Lineage" | "Class" | "Subclass" | "Feat" | "Gift" | "Item" | "Magic Item Keyword" | "Spell" | "") => {
                    const dt_info = DATA_TYPE_INFO.find(o => o.name === changed);
                    if (dt_info) {
                      setLoadingBlockables(true);
                      const archives = source_books.filter(o => obj.character_options.source_books.includes(o._id) && o.archived).map(o => o._id);
                      // debugger;
                      api.getObjects({ 
                        data_type: dt_info.data_type, 
                        archives,
                        log_it: true
                      }).then(res => {
                        setBlockableOptions(res as ModelBase[]);
                        setBlockingDataType(changed);
                        setPageNum(0);
                        setStartLetter("");
                        setBlockFilter({});
                        setFiltering(true);
                        setReloading(true);
                        setLoadingBlockables(false);
                      });
                    }
                  }}
                />
              </Grid>
              { loading_blockables ?
                <Grid
                  size={{
                    xs: 12
                  }}><LoadingBox /></Grid>
              : dt_info && 
                <>
                  <Grid
                    className='header centered'
                    size={{
                      xs: 12
                    }}>
                    Blocked { dt_info.plural }
                  </Grid>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <FilterBox
                      data_type={dt_info.data_type}
                      the_filter={block_filter}
                      dont_use_api
                      onChange={updateBlockFilter}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
                  </Grid>
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    { !reloading && filtered_and_paged.map((model_base, key) => {
                      return (
                        <Grid
                          className="padded"
                          key={key}
                          size={{
                            xs: 4
                          }}>
                          <ToggleButtonBox 
                            name={model_base.true_name}
                            value={character_options.blocked.includes(model_base._id)} 
                            disabled={is_disabled}
                            onToggle={() => {
                              const new_character_options = character_options;
                              if (new_character_options.blocked.includes(model_base._id)) {
                                new_character_options.blocked = new_character_options.blocked.filter(o => o !== model_base._id);
                              } else {
                                new_character_options.blocked.push(model_base._id);
                              }
                              setCharacterOptions(new_character_options);
                              setReloading(true);
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <PageLinks selected={page_num}
                      page_count={page_count}
                      onChange={setPageNum}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <LetterLinks 
                      onChange={setStartLetter} 
                    />
                  </Grid>
                </>
              }
            </>
          ); 
        }
      }
    }
    return return_me;
  }
  
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    const menu = ["Allowed Sources","Blocked Options","Special Rules"];
    return (
      <Grid container spacing={0} direction="row" className="bg_black_transp">
        { obj instanceof Character && obj.campaign && 
          <Grid>
            Settings inherited from { obj.campaign.name } campaign
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={menu}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        {/* { view === "Allowed Sources" && menu2.length > 0 &&
          <Grid item xs={12}>
            <FloatMenu
              options={menu2}
              selected={sb_view}
              onSelect={setSBView}
            />
          </Grid>
        } */}
        { render_tab() }
        { !is_disabled &&
          <Grid
            size={{
              xs: 12
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={ () => { 
                // debugger;
                if (obj instanceof Character) {
                  setProcessing(true);
                }
                onChange(character_options);
              }}>
              Submit
            </Button>
            <Button
              variant="contained"
              style={{ marginLeft: "4px" }}
              disabled={processing}
              onClick={ () => { 
                setCharacterOptions(obj.character_options.clone());
              }}>
              Cancel
            </Button>
          </Grid>
        }
        { processing &&
          <Grid size={12}>Saving Changes.  Will Reload when complete.</Grid>
        }
      </Grid>
    ); 
  }
}
